import React, { useState } from 'react';
import {
  Box,
  Typography,
  Alert,
} from '@mui/material';
import LocationDetails from '../LocationDetails';
import LocationTree from './LocationTree';
import CenterPointDialog from './CenterPointDialog';
import { Location, Place, LocationType } from '../../../types/interfaces';
import {
  useUpdateLocationBoundsMutation,
  useUpdatePlaceBoundsMutation,
  useUpdateLocationCenterPointMutation,
  useAddFavoriteLocationMutation,
  useRemoveFavoriteLocationMutation,
  useGetFavoriteLocationsQuery,
} from '../../../redux/adminApiSlice';

interface LocationEditorTabProps {
  locationTree: (Location | Place)[];
  selectedLocation: Location | Place | null;
  onLocationSelect: (nodeId: string) => void;
  getLocationIssues: (node: Location | Place, locationTree: (Location | Place)[]) => string[];
  onLocationUpdated: () => void;
}

const LocationEditorTab: React.FC<LocationEditorTabProps> = ({
  locationTree,
  selectedLocation,
  onLocationSelect,
  getLocationIssues,
  onLocationUpdated,
}) => {
  // State moved from LocationDashboard
  const [cornerMarkers, setCornerMarkers] = useState<{[key: string]: google.maps.LatLngLiteral}>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [visibleNeighborhoods, setVisibleNeighborhoods] = useState<{
    id: string;
    name: string;
    path: google.maps.LatLngLiteral[];
    isActive: boolean;
  }[]>([]);
  const [showCenterPointDialog, setShowCenterPointDialog] = useState(false);
  const [visibleLocations, setVisibleLocations] = useState<string[]>([]);
  const [favoriteLocations, setFavoriteLocations] = useState<string[]>([]);
  const [sidebarWidth, setSidebarWidth] = useState(300);
  const [isResizing, setIsResizing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // RTK Query hooks
  const [updateLocationBounds] = useUpdateLocationBoundsMutation();
  const [updatePlaceBounds] = useUpdatePlaceBoundsMutation();
  const [updateLocationCenterPoint] = useUpdateLocationCenterPointMutation();
  const [addFavoriteLocation] = useAddFavoriteLocationMutation();
  const [removeFavoriteLocation] = useRemoveFavoriteLocationMutation();
  const { data: favoriteLocationsData } = useGetFavoriteLocationsQuery();

  // Functions moved from LocationDashboard
  const handleMarkerDrag = (position: google.maps.LatLngLiteral, markerId: string) => {
    setCornerMarkers(prev => ({
      ...prev,
      [markerId]: position
    }));
    setHasUnsavedChanges(true);
  };

  const handleSaveBoundingBox = async (locationId: string, points: google.maps.LatLngLiteral[]) => {
    try {
      const closedPoints = [...points];
      if (points.length >= 3 && JSON.stringify(points[0]) !== JSON.stringify(points[points.length - 1])) {
        closedPoints.push({ ...points[0] });
      }

      const boundingBox = {
        type: 'Polygon' as const,
        coordinates: [[
          ...closedPoints.map(point => [point.lng, point.lat])
        ]]
      };

      // Determine if this is a Place or Location
      // Places have 'creator' property, Locations don't
      const isPlace = selectedLocation && 'creator' in selectedLocation;
      
      if (isPlace) {
        await updatePlaceBounds({ id: locationId, boundingBox }).unwrap();
      } else {
        await updateLocationBounds({ id: locationId, boundingBox }).unwrap();
      }
      
      setHasUnsavedChanges(false);
      setError(null);
      onLocationUpdated();
    } catch (error: any) {
      console.error('Error saving bounding box:', error);
      setError(error.data?.message || 'Failed to save bounding box changes');
    }
  };

  const handleSetCenterPoint = async (locationId: string, lat: number, lng: number) => {
    try {
      await updateLocationCenterPoint({
        id: locationId,
        coordinates: [lng, lat]
      }).unwrap();
      setShowCenterPointDialog(false);
      onLocationUpdated();
    } catch (error: any) {
      console.error('Error setting center point:', error);
      setError('Failed to set center point');
    }
  };

  const fetchGoogleBounds = async () => {
    if (!selectedLocation) return;
    
    try {
      if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
        setError('Google Maps Geocoder not loaded');
        return;
      }
      
      const geocoder = new window.google.maps.Geocoder();
      let searchQuery = selectedLocation.name;
      // Normalize casing: place nodes are tagged "PLACE" (uppercase), locations
      // are stored title-case ("Neighborhood", "City", ...).
      const level = ('level' in selectedLocation ? String(selectedLocation.level) : 'Place').toUpperCase();

      if (level === 'NEIGHBORHOOD') {
        searchQuery += ' neighborhood, Manhattan, New York City, NY';
      } else if (level === 'PLACE') {
        searchQuery += ', Manhattan, New York City, NY';
      } else if (level === 'CITY') {
        searchQuery += ', New York State';
      } else if (level === 'STATE') {
        searchQuery += ', United States';
      }

      const request: google.maps.GeocoderRequest = {
        address: searchQuery,
        componentRestrictions: { country: 'US' }
      };

      if (level === 'NEIGHBORHOOD' || level === 'PLACE') {
        request.bounds = new window.google.maps.LatLngBounds(
          { lat: 40.699392, lng: -74.020208 },
          { lat: 40.878195, lng: -73.907005 }
        );
      }

      const result = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
        geocoder.geocode(request, (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            resolve(results);
          } else {
            reject(new Error(`Geocoding failed: ${status}`));
          }
        });
      });

      if (result[0].geometry) {
        const bounds = result[0].geometry.bounds || result[0].geometry.viewport;
        
        if (bounds) {
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          
          setCornerMarkers({
            nw: { lat: ne.lat(), lng: sw.lng() },
            ne: { lat: ne.lat(), lng: ne.lng() },
            se: { lat: sw.lat(), lng: ne.lng() },
            sw: { lat: sw.lat(), lng: sw.lng() }
          });
          
          setHasUnsavedChanges(true);
          setError(null);
        } else {
          setError('No boundary data found');
        }
      } else {
        setError('No location data found');
      }
    } catch (error) {
      console.error('Error fetching Google bounds:', error);
      setError('Failed to fetch bounds from Google Maps');
    }
  };

  const calculateCenterFromBoundingBox = async (location: Location) => {
    if (!location.boundingBox?.coordinates?.[0]) return;

    const coordinates = location.boundingBox.coordinates[0];
    let sumLat = 0;
    let sumLng = 0;
    
    coordinates.forEach(([lng, lat]) => {
      sumLat += lat;
      sumLng += lng;
    });

    const centerLat = sumLat / (coordinates.length - 1);
    const centerLng = sumLng / (coordinates.length - 1);

    try {
      await updateLocationCenterPoint({
        id: location._id,
        coordinates: [centerLng, centerLat]
      }).unwrap();
      onLocationUpdated();
    } catch (error: any) {
      console.error('Error setting center point:', error);
      setError('Failed to set center point');
    }
  };

  const toggleLocationVisibility = (node: Location) => {
    if (!node.boundingBox) return;
    
    const coordinates = node.boundingBox.coordinates[0];
    const path = coordinates.map(([lng, lat]) => ({ lat, lng }));
    
    setVisibleNeighborhoods(prev => {
      const exists = prev.find(n => n.id === node._id);
      if (exists) {
        setVisibleLocations(prev => prev.filter(id => id !== node._id));
        return prev.filter(n => n.id !== node._id);
      }
      setVisibleLocations(prev => [...prev, node._id]);
      return [...prev, {
        id: node._id,
        name: node.name,
        path,
        isActive: false
      }];
    });
  };

  const toggleLocationFavorite = async (node: Location) => {
    try {
      if (favoriteLocationsData?.favoriteLocations.includes(node._id)) {
        const response = await removeFavoriteLocation(node._id).unwrap();
        if (response.success) {
          setFavoriteLocations(response.favoriteLocations || []);
        }
      } else {
        const response = await addFavoriteLocation(node._id).unwrap();
        if (response.success) {
          setFavoriteLocations(response.favoriteLocations || []);
        }
      }
    } catch (error: any) {
      console.error('Error toggling favorite:', error);
      setError('Failed to update favorites. Please try again.');
    }
  };

  const handleSubmit = (lat: number, lng: number) => {
    if (selectedLocation && 'level' in selectedLocation) {
      handleSetCenterPoint(selectedLocation._id, lat, lng);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);

    const startX = e.pageX;
    const startWidth = sidebarWidth;

    const handleMouseMove = (e: MouseEvent) => {
      if (isResizing) {
        const newWidth = Math.max(200, Math.min(600, startWidth + (e.pageX - startX)));
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Update favoriteLocations when data changes
  React.useEffect(() => {
    if (favoriteLocationsData?.favoriteLocations) {
      setFavoriteLocations(favoriteLocationsData.favoriteLocations);
    }
  }, [favoriteLocationsData]);

  return (
    <Box sx={{ 
      display: 'flex', 
      height: 'calc(100vh - 48px)',
      userSelect: isResizing ? 'none' : 'auto',
    }}>
      <Box sx={{ 
        width: sidebarWidth,
        minWidth: sidebarWidth,
        p: 2, 
        borderRight: 1, 
        borderColor: 'divider',
        overflow: 'auto',
        bgcolor: 'background.paper',
        position: 'relative',
      }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Favorites ({favoriteLocations.length})
          </Typography>
          {favoriteLocations.length > 0 ? (
            <LocationTree
              nodes={[{
                _id: 'favorites-root',
                name: 'Favorite Locations',
                level: 'Country' as LocationType,
                children: locationTree.flatMap(node => {
                  const flattenLocations = (node: Location | Place): (Location | Place)[] => {
                    if (!('level' in node)) return [node];
                    return [node, ...(node.children || []).flatMap(flattenLocations)];
                  };
                  return flattenLocations(node);
                }).filter(loc => favoriteLocations.includes(loc._id))
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(location => ({ ...location, children: [] }))
              }]}
              selectedLocation={selectedLocation?._id}
              onNodeSelect={onLocationSelect}
              getLocationIssues={getLocationIssues}
              onToggleVisibility={toggleLocationVisibility}
              onToggleFavorite={toggleLocationFavorite}
              visibleLocations={visibleLocations}
              favoriteLocations={favoriteLocations}
              showFavoritesOnly={true}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
              No favorite locations yet
            </Typography>
          )}
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Locations
          </Typography>
          <LocationTree
            nodes={locationTree}
            selectedLocation={selectedLocation?._id}
            onNodeSelect={onLocationSelect}
            getLocationIssues={getLocationIssues}
            onToggleVisibility={toggleLocationVisibility}
            onToggleFavorite={toggleLocationFavorite}
            visibleLocations={visibleLocations}
            favoriteLocations={favoriteLocations}
            showFavoritesOnly={false}
          />
        </Box>
      </Box>

      <Box
        sx={{
          width: '4px',
          bgcolor: isResizing ? 'primary.main' : 'transparent',
          cursor: 'col-resize',
          transition: 'background-color 0.2s',
          '&:hover': {
            bgcolor: 'primary.light',
          },
          zIndex: 1,
        }}
        onMouseDown={handleMouseDown}
      />

      <Box sx={{ 
        flexGrow: 1, 
        p: 2, 
        overflow: 'auto',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        '& .location-details-map': {
          flexGrow: 1,
          minHeight: '600px',
          height: 'calc(100vh - 150px)',
        }
      }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {!selectedLocation && (
          <Typography variant="body1" color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
            Select a location from the tree to view its details
          </Typography>
        )}
        
        {selectedLocation && (
          <LocationDetails
            location={selectedLocation}
            cornerMarkers={cornerMarkers}
            handleMarkerDrag={handleMarkerDrag}
            visibleNeighborhoods={visibleNeighborhoods}
            handleSetCenterPoint={handleSetCenterPoint}
            handleSaveBoundingBox={handleSaveBoundingBox}
            fetchGoogleBounds={fetchGoogleBounds}
            calculateCenterFromBoundingBox={calculateCenterFromBoundingBox}
            setShowCenterPointDialog={setShowCenterPointDialog}
            hasUnsavedChanges={hasUnsavedChanges}
            setHasUnsavedChanges={setHasUnsavedChanges}
            getLocationIssues={getLocationIssues}
            locationTree={locationTree}
            onLocationUpdated={onLocationUpdated}
          />
        )}
      </Box>

      <CenterPointDialog
        open={showCenterPointDialog}
        onClose={() => setShowCenterPointDialog(false)}
        handleSubmit={handleSubmit}
      />
    </Box>
  );
};

export default LocationEditorTab; 